import { useQuery } from "@tanstack/react-query";
import {
  type KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { devicesApi, type DeviceListRow } from "../../lib/api/devices";
import { deviceDisplayName, devicePickerMeta } from "../../lib/devices/display";
import { t } from "../../lib/i18n";
import { SearchInput } from "../ui/SearchInput";
import "../ui/ui.css";

type DevicePickerProps = {
  excludeDeviceIds?: string[];
  onSelect: (device: DeviceListRow) => void;
  disabled?: boolean;
};

export function DevicePicker({ excludeDeviceIds = [], onSelect, disabled }: DevicePickerProps) {
  const listId = useId();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const excludeSet = useMemo(() => new Set(excludeDeviceIds), [excludeDeviceIds]);

  const devicesQuery = useQuery({
    queryKey: ["device-picker", debouncedSearch],
    queryFn: () =>
      devicesApi.list({
        search: debouncedSearch || undefined,
        limit: debouncedSearch ? 25 : 15,
      }),
  });

  const results = useMemo(() => {
    const rows = devicesQuery.data ?? [];
    return rows.filter((d) => !excludeSet.has(d.id));
  }, [devicesQuery.data, excludeSet]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [debouncedSearch, results.length]);

  const pick = (device: DeviceListRow) => {
    if (disabled) return;
    onSelect(device);
    setSearch("");
    setDebouncedSearch("");
  };

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const device = results[highlightIndex];
      if (device) pick(device);
    }
  };

  useEffect(() => {
    const el = resultsRef.current?.querySelector<HTMLElement>(
      `[data-picker-index="${highlightIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex]);

  return (
    <div className="device-picker">
      <SearchInput
        placeholder={t("network.deviceGroups.member.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={onSearchKeyDown}
        disabled={disabled}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-expanded={results.length > 0}
      />
      <div
        id={listId}
        ref={resultsRef}
        className="device-picker__results"
        role="listbox"
        aria-label={t("network.deviceGroups.member.add.device")}
      >
        {devicesQuery.isLoading && (
          <p className="device-picker__hint">{t("common.loading")}</p>
        )}
        {devicesQuery.isError && (
          <p className="device-picker__hint device-picker__hint--error">{t("common.error")}</p>
        )}
        {!devicesQuery.isLoading && !devicesQuery.isError && results.length === 0 && (
          <p className="device-picker__hint">{t("network.deviceGroups.member.noResults")}</p>
        )}
        {results.map((device, index) => (
          <button
            key={device.id}
            type="button"
            role="option"
            data-picker-index={index}
            aria-selected={index === highlightIndex}
            className={`device-picker__option${index === highlightIndex ? " device-picker__option--active" : ""}`}
            onClick={() => pick(device)}
            disabled={disabled}
          >
            <span className="device-picker__option-title">{deviceDisplayName(device)}</span>
            <span className="device-picker__option-meta">{devicePickerMeta(device)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
